/**
 * Storage API — presigned URL upload from browser directly to S3.
 *
 * Phase 5 of multi-server expansion.
 * Uses the Phase 2 backend endpoints to request presigned URLs,
 * then uploads directly to S3 (Hetzner Object Storage) from the browser.
 *
 * AUTH CONTRACT:
 * All backend requests include Authorization: Bearer <token> from localStorage.
 *
 * UPLOAD FLOW:
 *   1. POST /api/uploads/request  → { asset_id, upload_url, expires_in }
 *   2. PUT  <upload_url>          → upload file directly to S3
 *   3. POST /api/uploads/confirm  → MediaAssetResponse (verified in S3)
 *
 * DOWNLOAD:
 *   GET /api/media/{asset_id}/url → { download_url, expires_in }
 */

import { getAuthHeaders } from './authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ── Step 1: Request presigned upload URL ──────────────────────────────────

/**
 * Request a presigned PUT URL for uploading a file to S3.
 * Creates a media asset record in Postgres and returns the upload URL.
 *
 * @param {string} kind - Asset kind: "video" | "image" | "background"
 * @param {string} contentType - MIME type (e.g. "video/mp4")
 * @param {string} filename - Original filename
 * @param {number} sizeBytes - File size in bytes
 * @returns {Promise<{asset_id: string, upload_url: string, expires_in: number}>}
 */
export async function requestUpload(kind, contentType, filename, sizeBytes) {
  const response = await fetch(`${BACKEND_URL}/api/uploads/request`, {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      kind,
      content_type: contentType,
      filename,
      size_bytes: sizeBytes,
    }),
  });

  if (!response.ok) {
    const status = response.status;
    let detail = `Request failed with status ${status}`;
    try {
      // Clone before reading — prevents "body stream already read" if
      // a platform script / browser extension consumed the original body.
      const text = await response.clone().text();
      try {
        const json = JSON.parse(text);
        detail = json.detail || text;
      } catch { detail = text || detail; }
    } catch {
      // Body already consumed or network issue — build detail from status
      if (status === 413) detail = 'Storage quota exceeded — try removing unused videos first.';
      else if (status === 429) detail = 'Too many concurrent uploads — please wait.';
      else if (status === 401) detail = 'Authentication required — please reload the page.';
      else if (status === 503) detail = 'Storage service temporarily unavailable.';
    }
    const err = new Error(`Upload request failed (${status}): ${detail}`);
    err.httpStatus = status;
    throw err;
  }

  return response.json();
}

// ── Step 2: Upload file directly to S3 via presigned URL ─────────────────

/**
 * Upload a file directly to S3 using a presigned PUT URL.
 * This sends the file from the browser to S3, bypassing the backend entirely.
 *
 * Uses XMLHttpRequest for upload progress tracking.
 *
 * @param {string} uploadUrl - Presigned PUT URL from requestUpload()
 * @param {File} file - File to upload
 * @param {string} contentType - MIME type (must match the presigned URL)
 * @param {function} [onProgress] - Progress callback: (percent: number) => void
 * @returns {Promise<void>} Resolves when upload completes
 */
export function uploadToS3(uploadUrl, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);

    // S3 presigned PUT requires the Content-Type to match what was signed
    xhr.setRequestHeader('Content-Type', contentType);

    // Track upload progress
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        // Include response body for better diagnostics
        let detail = xhr.statusText || 'Unknown error';
        try {
          // S3 returns XML error bodies
          const text = xhr.responseText;
          if (text) {
            const codeMatch = text.match(/<Code>(.*?)<\/Code>/);
            const msgMatch = text.match(/<Message>(.*?)<\/Message>/);
            if (codeMatch || msgMatch) {
              detail = `${codeMatch?.[1] || 'Error'}: ${msgMatch?.[1] || detail}`;
            }
          }
        } catch { /* ignore parse errors */ }
        console.error(`[S3Upload] PUT failed: ${xhr.status} ${detail}`, { url: uploadUrl.split('?')[0] });
        reject(new Error(`S3 upload failed (${xhr.status}): ${detail}`));
      }
    };

    xhr.onerror = () => {
      console.error('[S3Upload] Network error during PUT', { url: uploadUrl.split('?')[0] });
      reject(new Error('S3 upload failed: network error — check your connection'));
    };
    xhr.ontimeout = () => {
      console.error('[S3Upload] Timeout during PUT', { url: uploadUrl.split('?')[0] });
      reject(new Error('S3 upload timed out — try a smaller file or check your connection'));
    };
    xhr.timeout = 300000; // 5 minute timeout for large files

    xhr.send(file);
  });
}

// ── Step 3: Confirm upload in backend ────────────────────────────────────

/**
 * Confirm that a file was successfully uploaded to S3.
 * The backend verifies the file exists in S3 (via head_object)
 * and marks the asset as "confirmed".
 *
 * @param {string} assetId - Asset ID from requestUpload()
 * @returns {Promise<Object>} MediaAssetResponse
 */
export async function confirmUpload(assetId) {
  const response = await fetch(`${BACKEND_URL}/api/uploads/confirm`, {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ asset_id: assetId }),
  });

  if (!response.ok) {
    let detail = `Confirmation failed with status ${response.status}`;
    try {
      const text = await response.clone().text();
      try {
        const json = JSON.parse(text);
        detail = json.detail || text;
      } catch { detail = text || detail; }
    } catch { /* body already consumed */ }
    throw new Error(`Upload confirmation failed (${response.status}): ${detail}`);
  }

  return response.json();
}

// ── Download URL ─────────────────────────────────────────────────────────

/**
 * Get a presigned download URL for a confirmed asset.
 * The URL is generated at read time and expires after 1 hour.
 *
 * @param {string} assetId - Confirmed asset ID
 * @returns {Promise<{asset_id: string, download_url: string, expires_in: number}>}
 */
export async function getDownloadUrl(assetId) {
  const response = await fetch(`${BACKEND_URL}/api/media/${assetId}/url`, {
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    let detail = `Download URL failed with status ${response.status}`;
    try {
      const text = await response.clone().text();
      try {
        const json = JSON.parse(text);
        detail = json.detail || text;
      } catch { detail = text || detail; }
    } catch { /* body already consumed */ }
    throw new Error(`Download URL failed (${response.status}): ${detail}`);
  }

  return response.json();
}

// ── Convenience: Full video upload flow ──────────────────────────────────

/**
 * Complete flow: upload a video file to S3 via presigned URLs.
 *
 * Steps:
 *   1. Request presigned PUT URL from backend
 *   2. Upload file directly to S3
 *   3. Confirm upload in backend
 *   4. Get presigned download URL for preview
 *
 * @param {File} file - Video file to upload
 * @param {function} [onProgress] - Upload progress callback: ({ phase, percent, message }) => void
 * @returns {Promise<{asset_id: string, download_url: string, asset: Object}>}
 */
export async function uploadVideoToS3(file, onProgress) {
  const notify = onProgress || (() => {});

  // Step 1: Request presigned URL
  notify({ phase: 'requesting', percent: 0, message: 'Preparing upload...' });

  const { asset_id, upload_url, quota_warning } = await requestUpload(
    'video',
    file.type,
    file.name,
    file.size,
  );

  // Step 2: Upload to S3
  notify({ phase: 'uploading', percent: 0, message: 'Uploading to storage...' });

  await uploadToS3(upload_url, file, file.type, (percent) => {
    notify({ phase: 'uploading', percent, message: `Uploading... ${percent}%` });
  });

  // Step 3: Confirm upload
  notify({ phase: 'confirming', percent: 100, message: 'Verifying upload...' });

  const asset = await confirmUpload(asset_id);

  // Step 4: Get download URL for preview
  notify({ phase: 'ready', percent: 100, message: 'Ready' });

  const { download_url } = await getDownloadUrl(asset_id);

  return { asset_id, download_url, asset, quota_warning };
}

// ── Asset list ───────────────────────────────────────────────────────────

/**
 * Delete a media asset from S3 storage and Postgres.
 * The backend removes the S3 object and soft-deletes the Postgres record.
 * Only the asset owner can delete it.
 *
 * NOTE: Phase 5b — this is kept for backward compatibility but should NOT
 * be called from the VideoWidgetEditor anymore. Asset lifecycle is now
 * managed server-side via reference counting.
 *
 * @param {string} assetId - Asset ID to delete
 * @returns {Promise<{deleted: boolean, asset_id: string}>}
 */
export async function deleteAsset(assetId) {
  const response = await fetch(`${BACKEND_URL}/api/media/${assetId}`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    let detail = `Delete failed with status ${response.status}`;
    try {
      const text = await response.clone().text();
      try {
        const json = JSON.parse(text);
        detail = json.detail || text;
      } catch { detail = text || detail; }
    } catch { /* body already consumed */ }
    throw new Error(`Delete failed (${response.status}): ${detail}`);
  }

  return response.json();
}

// ── Batch resolve asset URLs (Phase 5b) ─────────────────────────────────

/**
 * Batch resolve asset_ids to presigned download URLs.
 * Used by useAssetHydration to hydrate video widget src at runtime.
 *
 * For each asset_id, returns either a download_url or a reason:
 *   - confirmed → { download_url, expires_in }
 *   - not_found → { reason: "not_found" }
 *   - deleted → { reason: "deleted" }
 *   - unconfirmed → { reason: "unconfirmed" }
 *
 * @param {string[]} assetIds - Array of asset_ids to resolve (max 50)
 * @returns {Promise<{items: Array<{asset_id: string, download_url?: string, reason?: string, expires_in?: number}>}>}
 */
export async function resolveAssets(assetIds) {
  if (!assetIds || assetIds.length === 0) {
    return { items: [] };
  }

  const response = await fetch(`${BACKEND_URL}/api/media/resolve`, {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ asset_ids: assetIds }),
  });

  if (!response.ok) {
    let detail = `Resolve failed with status ${response.status}`;
    try {
      const text = await response.clone().text();
      try {
        const json = JSON.parse(text);
        detail = json.detail || text;
      } catch { detail = text || detail; }
    } catch { /* body already consumed */ }

    // For 500 errors, return degraded response instead of throwing.
    // This prevents a backend DB error from crashing the entire carousel UI.
    // The caller (useAssetHydration) will see missing download_urls and
    // surface the error via resolveErrors state.
    if (response.status >= 500) {
      console.error(`[resolveAssets] Server error (${response.status}): ${detail}`);
      return {
        items: assetIds.map(id => ({
          asset_id: id,
          download_url: null,
          reason: 'server_error',
        })),
        _serverError: true,
        _errorDetail: detail,
      };
    }

    throw new Error(`Resolve failed (${response.status}): ${detail}`);
  }

  return response.json();
}

// ── List assets ──────────────────────────────────────────────────────────

/**
 * List the current user's media assets.
 *
 * @param {string} [kind] - Optional filter: "video" | "image" | "background"
 * @param {number} [limit=100] - Max results
 * @returns {Promise<Array>} Array of MediaAssetResponse objects
 */
export async function listAssets(kind, limit = 100) {
  const params = new URLSearchParams();
  if (kind) params.set('kind', kind);
  if (limit) params.set('limit', String(limit));

  const qs = params.toString();
  const url = `${BACKEND_URL}/api/media${qs ? '?' + qs : ''}`;

  const response = await fetch(url, {
    headers: await getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to list assets (${response.status})`);
  }

  return response.json();
}
